// What the site's pages and the embed card (embed.ts) both need, in a module
// of its own so the card's bundle doesn't take in the site's.

// The API: the local one under `wrangler dev`. Node (build.ts, the tests) has
// no `location`, and gets the real one.
const host = (globalThis as { location?: { hostname: string } }).location?.hostname;
export const API =
  host === "localhost" || host === "127.0.0.1" ? "http://localhost:8787" : "https://api.steamdata.dev";

const HTML_ESCAPES: Readonly<Record<string, string>> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;"
};

export function escapeHtml(text: unknown): string {
  return String(text).replace(/[&<>"']/g, (c) => HTML_ESCAPES[c] ?? c);
}

// A game's header image. Images load straight from Steam's CDN. header_image
// is the store's real URL (the crawler fetches it daily); until it's known,
// try the legacy path, which only exists for older apps.
export const headerSrc = (game: { app_id: number; header_image: string | null }): string =>
  game.header_image || `https://cdn.cloudflare.steamstatic.com/steam/apps/${game.app_id}/header.jpg`;

// A figure's arrow by which way it moved.
export const WAYS = { up: "▲ ", down: "▼ ", flat: "" } as const;

// Which way a figure moved: a key of WAYS.
export type Way = keyof typeof WAYS;

// Which way a signed figure moved, as a WAYS key.
export function wayOf(n: number): Way {
  return n > 0 ? "up" : n < 0 ? "down" : "flat";
}

// A change from the API as a ratio (day_change, an update's change), as
// { ratio, digits, way }; null when the API had no comparison to make, which
// for the 24h change is an app tracked for less than a day, one whose day-ago
// hour is missing, or a change from zero, which has no percentage.
export interface Change {
  ratio: number;
  digits: 0 | 1;
  way: Way;
}

export function dayChange(ratio: number | null | undefined): Change | null {
  if (ratio == null) return null;

  const digits = Math.abs(ratio) < 0.1 ? 1 : 0;
  const rounded = Number((ratio * 100).toFixed(digits));

  return { ratio, digits, way: wayOf(rounded) };
}

// A change's formats, by the decimal places it's shown to (Change's digits):
// tenths below 10%, whole percent above, where a fraction is noise.
export function percentFormats(
  locale: string | undefined,
  signDisplay: Intl.NumberFormatOptions["signDisplay"]
): [Intl.NumberFormat, Intl.NumberFormat] {
  const format = (digits: Change["digits"]): Intl.NumberFormat =>
    new Intl.NumberFormat(locale, {
      style: "percent",
      signDisplay,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits
    });
  return [format(0), format(1)];
}

// A change as its arrow and signed percent, in `formats` (percentFormats).
export const changeText = ({ ratio, digits, way }: Change, formats: readonly Intl.NumberFormat[]): string =>
  `${WAYS[way]}${formats[digits]?.format(ratio) ?? ""}`;
